/**
 * Static, local page content for the Statistiques page.
 *
 * Live, from `data.ulule.com/stats.json`: the hero figures, the success rate, the average
 * contribution, the category ledger.
 *
 * Live, from `collections.ts`: the two leaderboards and the biggest collection — the public project
 * search, mixed with the frozen `kkbbProjects.json`.
 *
 * Not live: the figures the feed does not carry, hand-maintained in `figures.json`. Everything else
 * here is copy.
 */

export const HERO = {
  breadcrumbTag: 'Statistiques',
  /** Aggregation caveat under the three headline figures. */
  foot: 'Ces chiffres agrègent les données d’Ulule et de KissKissBankBank (KKBB a rejoint Ulule en 2024).',
  thanks: 'Merci pour votre confiance',
  thanksCta: {
    before: 'Vous aussi, vous pouvez ',
    label: 'lancer votre collecte',
    href: '/projects/create/',
    after: ' !',
  },
}

export type SuccessRow = {
  /** Rendered as a counter; the unit is appended after the animated digits. */
  value: number
  suffix: string
  /** Sits before the digits, outside the animation — `+` reads the figure as a gain, not a level. */
  prefix?: string
  title: string
  body: string
  /** CSS-module class for the figure's colour; carried per row, not derived from position. */
  tone: string
}

export const SUCCESS = {
  kicker: 'Taux de réussite',
  bodies: {
    successRate:
      'Grâce à l’accompagnement Ulule et aux ressources proposées. Meilleur taux de succès crowdfunding au monde !',
    overshoot:
      'En moyenne, les collectes dépassent leur objectif. Ça tombe bien : sur Ulule, les commissions ne sont dues que si votre projet atteint ou dépasse son objectif. Lancer une collecte, c’est gratuit.',
    shareDriven:
      'Aussi appelé effet plateforme, ou effet Ulule 🦉. Les makers construisent en public et les internautes sont incités à partager leur contribution. L’effet partage amplifie la portée des collectes.',
  },
  titles: {
    successRate: 'Taux de réussite des collectes.',
    overshoot: 'Taux de dépassement moyen.',
    shareDriven: 'L’effet Partage.',
  },
  cta: { label: 'Je lance ma collecte', href: '/projects/create/' },
}

export const MONEY = {
  kicker: 'Montants',
  averageBody: 'C’est le montant moyen versé par les personnes qui participent à une collecte.',
  smallestBody:
    'C’est le plus petit montant collecté sur une collecte réussie. Sur Ulule, on aime tous les projets, petits et grands !',
  largestBodyBefore: 'C’est le plus gros montant collecté sur Ulule (voir ',
  largestBodyLink: 'le top 10',
  largestBodyAfter: ').',
}

export const CATEGORIES = {
  kicker: 'Par catégorie',
  lede: 'Projets entrepreneuriaux, créatifs, citoyens, projets de vie ou projets fous, ils sont tous sur Ulule. Voici les catégories les plus populaires :',
  note: 'Montants collectés depuis 2010, sur Ulule uniquement.',
}

export const STATUS = {
  kicker: 'Par statut',
  /** Fully static: a standing claim, not a figure anything will serve. */
  womenLedClaim: 'Sur Ulule, plus d’un projet sur deux est lancé par ',
  womenLedEmphasis: 'une femme',
  /** Rendered after the claim's full stop, so the emphasis stays on the preceding clause. */
  womenLedCoda: 'Et c’est très bien.',
  lede: 'Sur Ulule, il est possible de lancer une collecte quel que soit votre statut. Il y a beaucoup de personnes qui se lancent en tant que particuliers… parce qu’elles montent ensuite leur entreprise ou leur asso avec les fonds récoltés.',
  cta: { label: 'Je lance ma collecte', href: '/projects/create/' },
}

export const TOP_COLLECTIONS = {
  kicker: 'Les plus gros montants',
  lede: 'Ulule et KissKissBankBank confondus.',
  filterLabel: 'Catégorie',
  /** First and default option of the filter — the unfiltered board. */
  allCategories: 'Toutes les catégories',
  /** Stands in for the category on a filtered board — see `toTopEntry`. */
  kkbbPlatform: 'KissKissBankBank',
}

export const POPULAR_COLLECTIONS = {
  kicker: 'Le vent en poupe',
  cta: { label: 'En voir plus', href: '/discover/' },
}

export const CTA = {
  title: 'Je lance ma collecte',
  lede: 'Rejoignez les dizaines de milliers de porteurs et porteuses de projets financés sur Ulule.',
  action: { label: 'Je lance ma collecte', href: '/projects/create/' },
}

export const DIGGER = {
  kicker: 'Data digger',
  paragraphs: [
    'Plusieurs organismes universitaires ont exploré les data Ulule pour des travaux de recherche afin de mieux cerner la dynamique à l’œuvre sur la plateforme.',
    'Vous pouvez accéder à l’ensemble de nos data anonymisées en formulant une demande sur la page contact.',
  ],
  cta: { label: 'Formuler une demande', href: '/about/contact/' },
  aside: {
    title: 'À savoir',
    body: 'Les jeux de données transmis sont anonymisés et agrégés. Précisez l’objet de votre recherche. Le jeu de données Ulule porte sur la période 2015-2025.',
  },
}
